/** @jsxImportSource @emotion/react */
import * as s from '../../core/style';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GEOSERVER } from 'config/URL/URL';
import { getCenter } from 'ol/extent';
import TileLayer from 'ol/layer/Tile';
import TileWMS from 'ol/source/TileWMS';
import 'ol-ext/dist/ol-ext.min.css';
import PopulationChartModal from 'components/charts/PopulationChartModal';
import PopulationPopup from '../../popups/PopulationPopup';
import PopMoveRoutePopup from '../../popups/PopMoveRoutePopup';
import useBaseOlMap from 'hooks/useBaseOlMap';
import useMapOverlay from 'hooks/map/useMapOverlay';
import usePopulationSelection from 'hooks/map/usePopulationSelection';
import useMoveRouteState from 'hooks/map/useMoveRouteState';
import usePopulationLayer from 'hooks/map/usePopulationLayer';
import useMoveRouteLayer from 'hooks/map/useMoveRouteLayer';
import useViewportParams from 'hooks/map/useViewportParams';

const createBoundaryLayer = (layerName) =>
    new TileLayer({
        source: new TileWMS({
            url: GEOSERVER + '/geoserver/ows',
            params: {
                LAYERS: layerName,
                TILED: true,
                VERSION: '1.1.1',
                FORMAT: 'image/png',
                TRANSPARENT: true,
            },
            serverType: 'geoserver',
            transition: 0,
        }),
        visible: true,
    });


const resolvePopulationLayerName = (zoom = 0) => {
    if (zoom < 11) return 'sido';
    if (zoom < 14) return 'sgg';
    return 'emd';
};

const PopulationMap = ({ selectedAge, selectedFilter, lineLayerVisible, setLineLayerVisible}) => {
    const { mapRef, initializeMap, isMapReady } = useBaseOlMap({
        targetId: 'population_map_area',
        minZoom: 7,
        maxZoom: 18,
    });
    const boundaryLayerRef = useRef(null);
    const populationOverlayOptions = useMemo(() => ({ autoPan: false }), []);
    const overlayRef = useMapOverlay({
        mapRef,
        elementId: 'population_popup',
        isMapReady,
        options: populationOverlayOptions,
    });
    const moveRouteOverlayRef = useMapOverlay({
        mapRef,
        elementId: 'popmoveroute_popup',
        isMapReady,
    });
    const [ filter, setFilter ] = useState('popopulation');
    const viewportParams = useViewportParams({
        mapRef,
        isMapReady,
        resolveLayerName: resolvePopulationLayerName,
    });
    const mapLayerName = viewportParams?.layerName ?? 'sido';

    const {
        selectedLocation,
        setSelectedLocation,
        populationPopupState,
        setPopulationPopupState,
        handleLocationSelect,
    } = usePopulationSelection();

    const {
        movPopParams,
        setMovPopParams,
        selectedMoveRouteInfo,
        setSelectedMoveRouteInfo,
        selectedMoveRouteInfoItems,
        setSelectedMoveRouteInfoItems,
        resetMoveRouteInfoItems,
    } = useMoveRouteState();

    const [ popDetailModalIsOpen, setPopDetailModalIsOpen ] = useState(false);

    const {
        refreshPopulationLayer,
        isLoading: isPopulationLayerLoading,
        error: populationLayerError,
    } = usePopulationLayer({
        mapRef,
        filter,
        selectedAge,
        mapLayerName,
        extent: viewportParams?.extents,
        isMapReady,
    });

    const {
        drawMoveRoutes,
        clearMoveRoutes,
        isLoading: isMoveRouteLoading,
        error: moveRouteError,
    } = useMoveRouteLayer({
        mapRef,
        mapLayerName,
        selectedAge,
        moveRouteOverlayRef,
        setLineLayerVisible,
        setSelectedMoveRouteInfo,
        setSelectedMoveRouteInfoItems,
        resetMoveRouteInfoItems,
        setMovPopParams,
    });

    useEffect(() => {
        initializeMap();
    }, [initializeMap]);

    useEffect(() => {
        if (!selectedLocation?.feature) {
            setPopDetailModalIsOpen(false)
            overlayRef.current?.setPosition(undefined);
            return;
        }

        const coord = getCenter(selectedLocation.feature.getGeometry().getExtent());
        overlayRef.current?.setPosition(coord);
        setLineLayerVisible(false);

    }, [overlayRef, selectedLocation]);

    useEffect(() => {
        overlayRef.current?.setPosition(undefined);
        setSelectedLocation(undefined);
        setLineLayerVisible(false);
        setMovPopParams(undefined);
    }, [selectedAge, selectedFilter, mapLayerName])

    useEffect(() => {
        if (populationPopupState !== 'mov') {
            moveRouteOverlayRef.current?.setPosition(undefined);
        }
    }, [moveRouteOverlayRef, populationPopupState]);

    useEffect(() => {
        const map = mapRef.current;
        if (!map || !isMapReady || boundaryLayerRef.current) return;

        const boundaryLayer = createBoundaryLayer(mapLayerName);
        boundaryLayer.set('name', 'boundaryLayer');
        map.addLayer(boundaryLayer);
        boundaryLayerRef.current = boundaryLayer;
    }, [isMapReady, mapLayerName, mapRef]);

    useEffect(() => {
        if (boundaryLayerRef.current) {
            boundaryLayerRef.current.getSource().updateParams({
                LAYERS: mapLayerName,
            });
        }
    }, [mapLayerName]);

    useEffect(() => {
        if (!isMapReady) return;
        refreshPopulationLayer();
    }, [isMapReady, refreshPopulationLayer]);

    useEffect(() => {
        clearMoveRoutes({ preserveParams: true });
    }, [clearMoveRoutes, mapLayerName]);

    useEffect(() => {
        if (selectedFilter === null || selectedFilter === undefined) {
            setFilter('popopulation');
        } else {
            setFilter(selectedFilter.value);
        }
    }, [selectedFilter]);

    useEffect(() => {
        if (!movPopParams || !selectedLocation?.feature) {
            return;
        }

        drawMoveRoutes({ feature: selectedLocation.feature, params: movPopParams });
        const coord = getCenter(selectedLocation.feature.getGeometry().getExtent());
        moveRouteOverlayRef.current?.setPosition(coord);
        
        return () => {
            moveRouteOverlayRef.current?.setPosition(undefined);
        }
    }, [movPopParams]);

    useEffect(() => {
        if (lineLayerVisible === false) {
            clearMoveRoutes();
        }
    }, [clearMoveRoutes, lineLayerVisible]);

    const handleMapClick = useCallback(
        (evt) => {
            const map = mapRef.current;
            if (!map) return;

            const feature = map.forEachFeatureAtPixel(evt.pixel, (f) => f);
            if (feature && feature.getGeometry().getType().toLowerCase() === 'point') {
                const coord = getCenter(feature.getGeometry().getExtent());
                handleLocationSelect(feature);
                setPopDetailModalIsOpen(false);
                overlayRef.current?.setPosition(coord);
            } else {
                handleLocationSelect(null);
                setPopDetailModalIsOpen(false);
                overlayRef.current?.setPosition(undefined);
                moveRouteOverlayRef.current?.setPosition(undefined);
                setLineLayerVisible(false);
            }
        },
        [handleLocationSelect, mapRef, moveRouteOverlayRef, overlayRef, setPopDetailModalIsOpen]
    );

    useEffect(() => {
        const map = mapRef.current;
        if (!map || !isMapReady) return;

        map.on('click', handleMapClick);
        return () => {
            map.un('click', handleMapClick);
        };
    }, [handleMapClick, isMapReady, mapRef]);

    const handlePopupClose = () => {
        overlayRef.current?.setPosition(undefined);
    };

    const onClickPopDetailButton = () => {
        setPopDetailModalIsOpen(true);
        handlePopupClose();
    }

    const handleMoveRoutePopupClose = () => {
        moveRouteOverlayRef.current?.setPosition(undefined);
        setSelectedMoveRouteInfo(null);
        setLineLayerVisible(false);
    };

    const isMapBusy = isPopulationLayerLoading || isMoveRouteLoading;

    useEffect(() => {
        if (populationLayerError) {
            console.error('Population layer error', populationLayerError);
        }
    }, [populationLayerError]);

    useEffect(() => {
        if (moveRouteError) {
            console.error('Move route layer error', moveRouteError);
        }
    }, [moveRouteError]);

    return (
        <div css={s.mainContainer}>
            <div id="population_map_area" css={s.mapArea}>
                {isMapBusy && (
                    <div className="map-loading-overlay">
                        <span className="map-loading-text">데이터를 불러오는 중...</span>
                    </div>
                )}
            </div>
            
            {/* 인구이동특성 Rank Popup */}
            <PopMoveRoutePopup
                movPopParams={movPopParams}
                selectedMoveRouteInfo={selectedMoveRouteInfo}
                selectedMoveRouteInfoItems={selectedMoveRouteInfoItems}
                handleMoveRoutePopupClose={handleMoveRoutePopupClose}
                isOpen={lineLayerVisible}
            />

            {/* 인구현황 Popup */}
            <PopulationPopup 
                selectedLocation={selectedLocation}
                handlePopupClose={handlePopupClose}
                onClickPopDetailButton={onClickPopDetailButton}
                movPopParams={movPopParams}
                setMovPopParams={setMovPopParams}
                populationPopupState={populationPopupState}
                setPopulationPopupState={setPopulationPopupState}
                setLineLayerVisible={setLineLayerVisible}
            />

            {/* 인구현황 상세 Modal */}
            <PopulationChartModal
                isOpen={popDetailModalIsOpen}
                onClose={() => setPopDetailModalIsOpen(false)}
                location={selectedLocation}
            />
            
        </div>
    );
};

export default PopulationMap;
